import React from 'react'
import { cn } from '../../utils/cn'

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Visual variant
   */
  variant?: 'info' | 'success' | 'warning' | 'error' | 'neutral'

  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg'

  /**
   * Removable badge with close button
   */
  removable?: boolean

  /**
   * Callback when remove button is clicked
   */
  onRemove?: () => void

  /**
   * Icon to display before text
   */
  icon?: React.ReactNode
}

const badgeVariants = {
  variant: {
    info: 'bg-toast-gold/10 text-toast-gold border-toast-gold/30',
    success: 'bg-avocado-green/10 text-avocado-green border-avocado-green/30',
    warning: 'bg-honey-yellow/20 text-deep-charcoal border-honey-yellow/40',
    error: 'bg-coral-pop/10 text-coral-pop border-coral-pop/30',
    neutral: 'bg-soft-tan/30 text-deep-charcoal border-soft-tan',
  },
  size: {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2 py-1 text-xs',
    lg: 'px-3 py-1 text-sm',
  },
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant = 'neutral',
      size = 'md',
      removable = false,
      onRemove,
      icon,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <span
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center gap-1',
          'rounded-full border font-medium',
          'transition-colors',

          // Variant styles
          badgeVariants.variant[variant],

          // Size styles
          badgeVariants.size[size],

          // Custom className
          className
        )}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}

        <span>{children}</span>

        {removable && onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className="flex-shrink-0 ml-0.5 hover:opacity-70 transition-opacity"
            aria-label="Remove"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

export default Badge
